import { useCallback, useEffect, useRef, useState } from "react";

const WASM = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.2.0/wasm";
const MODEL = "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite";

async function loadDetector() {
  const { FaceDetector, FilesetResolver } = await import("@mediapipe/tasks-vision");
  const vision = await FilesetResolver.forVisionTasks(WASM);
  return FaceDetector.createFromOptions(vision, { baseOptions: { modelAssetPath: MODEL }, runningMode: "VIDEO", minDetectionConfidence: 0.55 });
}

// Yaw estimate from eye/nose keypoints: nose far from eye midpoint => head turned away.
function analyse(dets) {
  const faces = dets.length;
  if (faces !== 1) return { faces, away: false };
  const k = dets[0].keypoints || [];
  if (k.length < 3) return { faces, away: false };
  const eyeGap = Math.abs(k[1].x - k[0].x) || 0.001;
  const yaw = Math.abs(k[2].x - (k[0].x + k[1].x) / 2) / eyeGap;
  const pitch = (k[2].y - (k[0].y + k[1].y) / 2) / eyeGap;
  return { faces, away: yaw > 0.55 || pitch < 0.15 || pitch > 1.3 };
}

export function useProctor() {
  const streamRef = useRef(null);
  const videoEl = useRef(null);
  const detRef = useRef(null);
  const [camera, setCamera] = useState("idle");
  const [ai, setAi] = useState("idle");
  const [live, setLive] = useState({ faces: null, away: false });

  const videoRef = useCallback((el) => {
    videoEl.current = el;
    if (el && streamRef.current && el.srcObject !== streamRef.current) { el.srcObject = streamRef.current; el.play().catch(() => {}); }
  }, []);

  const enable = useCallback(async () => {
    setCamera("starting");
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480, facingMode: "user" }, audio: false });
      streamRef.current = s;
      if (videoEl.current) { videoEl.current.srcObject = s; await videoEl.current.play().catch(() => {}); }
      setCamera("on");
    } catch { setCamera("denied"); return; }
    setAi("loading");
    try { detRef.current = await loadDetector(); setAi("ready"); } catch { setAi("unavailable"); }
  }, []);

  const detect = useCallback(() => {
    const v = videoEl.current;
    if (!detRef.current || !v || v.readyState < 2) return null;
    try { const r = analyse(detRef.current.detectForVideo(v, performance.now()).detections); setLive(r); return r; } catch { return null; }
  }, []);

  const capture = useCallback(() => {
    const v = videoEl.current;
    if (!v || v.readyState < 2) return null;
    const c = document.createElement("canvas");
    c.width = 320; c.height = 240;
    c.getContext("2d").drawImage(v, 0, 0, 320, 240);
    return c.toDataURL("image/jpeg", 0.6);
  }, []);

  const cameraLost = useCallback(() => {
    const t = streamRef.current?.getVideoTracks()[0];
    return !t || t.readyState === "ended" || t.muted;
  }, []);

  useEffect(() => () => { streamRef.current?.getTracks().forEach((t) => t.stop()); detRef.current?.close?.(); }, []);

  return { videoRef, camera, ai, live, enable, detect, capture, cameraLost };
}
